import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";

type Msg = { from: "bot" | "user"; text: string };

const QUICK = [
  "Hvilke tjenester tilbyr dere?",
  "Hva koster det?",
  "Hvordan booker jeg time?",
  "Tilbyr dere dekkskift?",
];

function reply(q: string): string {
  const t = q.toLowerCase();
  if (t.includes("tjenester") || t.includes("tilbyr"))
    return "Vi tilbyr innvendig og utvendig vask, komplett vask, premiumpakke, motorromrens, felgrens, dekkskift, oljeskift, rustfjerning, polering og gull komplett, alt fra vårt anlegg i Drammen.";
  if (t.includes("kost") || t.includes("pris"))
    return "Prisene avhenger av bilstørrelse. Utvendig vask fra 250 kr, innvendig vask fra 400 kr, komplett vask fra 600 kr. Se full prisliste og velg bilstørrelse under booking.";
  if (t.includes("book") || t.includes("time"))
    return "Klikk på «Book time» øverst: velg tjeneste, fyll inn kontaktinfo og velg dato. Det tar under 2 minutter.";
  if (t.includes("dekkskift") || t.includes("dekk"))
    return "Ja, vi utfører dekkskift med hjulbalansering og oppbevaring på dekkhotell.";
  return "Takk for meldingen. I denne demoen kan jeg hjelpe med tjenester, priser og booking.";
}

export function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([
    { from: "bot", text: "Hei! Jeg er Drammen Car Wash Assistent. Spør om tjenester, priser eller booking." },
  ]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [msgs, typing]);

  const send = (text: string) => {
    if (!text.trim()) return;
    setMsgs((m) => [...m, { from: "user", text }]);
    setInput("");
    setTyping(true);
    const delay = 800 + Math.random() * 400;
    setTimeout(() => {
      setTyping(false);
      setMsgs((m) => [...m, { from: "bot", text: reply(text) }]);
    }, delay);
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-background border border-primary text-primary shadow-luxe animate-chat-pulse flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Åpne chat"
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-[380px] z-40 max-h-[80vh] rounded-2xl border border-border bg-popover/95 backdrop-blur-md shadow-luxe flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <div className="font-medium text-sm">Drammen Car Wash Assistent</div>
              <div className="text-xs text-muted-foreground">Spør om tjenester, priser eller booking.</div>
            </div>
            <button onClick={() => setOpen(false)} className="h-8 w-8 flex items-center justify-center rounded-full hover:bg-secondary" aria-label="Lukk">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[240px] max-h-[45vh]">
            {msgs.map((m, i) => (
              <div key={i} className={m.from === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                    m.from === "user" ? "bg-primary-strong text-primary-foreground" : "bg-secondary text-foreground"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {typing && (
              <div className="flex justify-start">
                <div className="bg-secondary rounded-2xl px-3.5 py-2.5 text-sm flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:120ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground animate-bounce [animation-delay:240ms]" />
                </div>
              </div>
            )}
          </div>
          <div className="px-3 pb-2 flex gap-1.5 flex-wrap">
            {QUICK.map((q) => (
              <button
                key={q}
                onClick={() => send(q)}
                className="text-xs rounded-full border border-border px-3 py-1.5 text-foreground/85 hover:border-primary hover:text-primary transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="flex items-center gap-2 border-t border-border p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Skriv en melding…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <button type="submit" className="h-9 w-9 rounded-full bg-primary-strong text-primary-foreground flex items-center justify-center" aria-label="Send">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}